import download from 'downloadjs'
import { combineEpics } from 'redux-observable'
import { Observable } from 'rxjs/Observable'

import { wrapCSVTextToBlob, xhrExtractFileName } from 'utils/helpers'

import { addToast } from 'containers/Toasts/actions'

import { downloadOriginalEmailError, downloadOriginalEmailSuccess } from '../Report/actions'

import {
  clusterUndoError,
  clusterUndoSuccess,
  downloadCsvError,
  downloadCsvSuccess,
  downloadEmailError,
  fetchDataError,
  fetchDataSuccess,
  revertBackToOriginError,
  revertBackToOriginSuccess,
  stopRemediationError,
  stopRemediationSuccess,
} from './actions'
import {
  ATTACHMENTS_AND_LINKS_FILTER_CHANGE,
  CLUSTER_UNDO_ASYNC,
  DATA_REQUEST_ASYNC,
  DOWNLOAD_CSV_REQUEST,
  DOWNLOAD_EMAIL_ASYNC,
  END_USERS_FILTER_CHANGE,
  getSuccessToastMessage,
  IPS_FILTER_CHANGE,
  IS_READ_FILTER_CHANGE,
  JUNK_MAIL_FILTER_CHANGE,
  MAILBOX_FILTER_CHANGE,
  NOT_FOUND_FILTER_CHANGE,
  PAGINATE_CHANGE,
  REVERT_BACK_TO_ORIGIN_ASYNC,
  SEARCH_CHANGE,
  SENDER_FILTER_CHANGE,
  SORT_PROPERTY_CHANGE,
  STOP_REMEDIATION_ASYNC,
  SUBJECT_FILTER_CHANGE,
  TOGGLE_RELEASE_FILTER,
} from './constants'

const getBodyForReclassify = (state) => ({
  reclusterAll: state.selectAll,
  senderNameFilter: state.selectAll ? state.senderFilter.map((elem) => elem.value) : '',
  emailSubjectFilter: state.selectAll ? state.subjectFilter.map((elem) => elem.value) : '',
  recipientAddressFilter: state.selectAll ? state.mailboxFilter.map((elem) => elem.value) : '',
  ipAddressFilter: state.selectAll ? state.ipsFilter.map((elem) => elem.value) : '',
  attachmentsAndLinksFilter: state.selectAll ? state.attachmentsAndLinksFilter : '',
  notFoundFilter: state.selectAll ? state.notFoundFilter : '',
  isReadFilter: state.selectAll ? state.isReadFilter : '',
  search: state.search,
  includeEmails: !state.selectAll ? state.includeEmails : [],
  excludeEmails: state.selectAll ? state.excludeEmails : [],
  firstMitigationId: parseInt(Object.keys(state.data.results)[0]),
})

const fetchDataEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$
    .ofType(
      DATA_REQUEST_ASYNC.INITIATE,
      SORT_PROPERTY_CHANGE,
      SEARCH_CHANGE,
      PAGINATE_CHANGE,
      SENDER_FILTER_CHANGE,
      SUBJECT_FILTER_CHANGE,
      MAILBOX_FILTER_CHANGE,
      IPS_FILTER_CHANGE,
      JUNK_MAIL_FILTER_CHANGE,
      NOT_FOUND_FILTER_CHANGE,
      ATTACHMENTS_AND_LINKS_FILTER_CHANGE,
      IS_READ_FILTER_CHANGE,
      CLUSTER_UNDO_ASYNC.SUCCESS,
      STOP_REMEDIATION_ASYNC.SUCCESS,
      REVERT_BACK_TO_ORIGIN_ASYNC.SUCCESS,
      TOGGLE_RELEASE_FILTER,
      END_USERS_FILTER_CHANGE
    )
    .filter(() => store.getState().resolutioncenter.affMailboxesDetail.reportId)
    .mergeMap(() => {
      const state = store.getState().resolutioncenter.affMailboxesDetail
      return api.resolutionCenter
        .getAffectedMailboxesDetail(
          state.reportId,
          {
            page: state.page || 1,
            search: state.search,
            sortProperty: state.sortProperty,
            senderNameFilter: state.senderFilter.map((elem) => elem.value),
            emailSubjectFilter: state.subjectFilter.map((elem) => elem.value),
            recipientAddressFilter: state.mailboxFilter.map((elem) => elem.value),
            ipAddressFilter: state.ipsFilter.map((elem) => elem.value),
            junkMailFilter: state.junkMailFilter.map((elem) => elem.value),
            attachmentsAndLinksFilter: state.attachmentsAndLinksFilter,
            notFoundFilter: state.notFoundFilter,
            isReadFilter: state.isReadFilter,
            includeCheckboxValues: true,
            reviewRequestedFilter: state.releaseRequestFilter,
            filterByEndUser: state.endUsersFilter,
          },
          false,
          store.getState().partnerOptions.selectedCompany?.value
        )
        .map((response) => fetchDataSuccess(response.response))
        .catch((error) => Observable.of(epicsErrorHandler(fetchDataError, error)))
        .takeUntil(action$.ofType(DATA_REQUEST_ASYNC.CANCEL))
    })

const downloadCsvEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DOWNLOAD_CSV_REQUEST.INITIATE).mergeMap(() => {
    const state = store.getState().resolutioncenter.affMailboxesDetail
    return api.resolutionCenter
      .getAffectedMailboxesDetail(
        state.reportId,
        {
          page: state.page || 1,
          search: state.search,
          sortProperty: state.sortProperty,
          senderNameFilter: state.senderFilter.map((elem) => elem.value),
          emailSubjectFilter: state.subjectFilter.map((elem) => elem.value),
          recipientAddressFilter: state.mailboxFilter.map((elem) => elem.value),
          ipAddressFilter: state.ipsFilter.map((elem) => elem.value),
          attachmentsAndLinksFilter: state.attachmentsAndLinksFilter,
          notFoundFilter: state.notFoundFilter,
          isReadFilter: state.isReadFilter,
          includeCheckboxValues: false,
          reviewRequestedFilter: state.releaseRequestFilter,
        },
        true,
        store.getState().partnerOptions.selectedCompany?.value
      )
      .map((response) => {
        download(
          wrapCSVTextToBlob(response.response),
          xhrExtractFileName(response.xhr),
          response.responseType
        )
        return downloadCsvSuccess(response)
      })
      .catch((error) => Observable.of(epicsErrorHandler(downloadCsvError, error)))
      .takeUntil(action$.ofType(DATA_REQUEST_ASYNC.CANCEL))
  })

const clusterUndoEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(CLUSTER_UNDO_ASYNC.INITIATE).mergeMap((action) => {
    const state = store.getState().resolutioncenter.affMailboxesDetail
    const body = getBodyForReclassify(state)

    return api.resolutionCenter
      .clusterUndo({ ...body, newState: action.payload.newState })
      .mergeMap((response) =>
        Observable.of(
          clusterUndoSuccess(response),
          addToast({ message: getSuccessToastMessage(response.response, true) })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(clusterUndoError, error),
          addToast({ message: error.response?.message, type: 'error' })
        )
      )
      .takeUntil(action$.ofType(CLUSTER_UNDO_ASYNC.CANCEL))
  })

const revertBackToOriginEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(REVERT_BACK_TO_ORIGIN_ASYNC.INITIATE).mergeMap(() => {
    const state = store.getState().resolutioncenter.affMailboxesDetail
    const body = getBodyForReclassify(state)
    return api.resolutionCenter
      .revertBackToOrigin(body)
      .mergeMap((response) =>
        Observable.of(
          revertBackToOriginSuccess(response),
          addToast({
            message: getSuccessToastMessage(response.response, false),
          })
        )
      )
      .catch((error) =>
        Observable.of(
          epicsErrorHandler(revertBackToOriginError, error),
          addToast({ message: error.response?.message, type: 'error' })
        )
      )
      .takeUntil(action$.ofType(REVERT_BACK_TO_ORIGIN_ASYNC.CANCEL))
  })

const stopRemediationEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(STOP_REMEDIATION_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .stopRemediation(
        { report: action.payload.reportId },
        store.getState().partnerOptions.selectedCompany?.value
      )
      .map((response) => stopRemediationSuccess(response))
      .catch((error) => Observable.of(epicsErrorHandler(stopRemediationError(), error)))
  )

const downloadEmailEpic = (action$, store, { api, epicsErrorHandler }) =>
  action$.ofType(DOWNLOAD_EMAIL_ASYNC.INITIATE).mergeMap((action) =>
    api.resolutionCenter
      .downloadIndividualEmail(action.payload.mitigationId)
      .map((response) => {
        download(response, `${action.payload.mitigationId}`, response?.responseType)
        return downloadOriginalEmailSuccess()
      })
      .catch((error) => {
        let parsedError = { message: 'Email cannot be downloaded' }
        if (error && error.response) {
          error.response.text().then((errorData) => {
            parsedError = JSON.parse(errorData)
          })
        }
        return Observable.of(
          epicsErrorHandler(
            () => downloadEmailError(error, action.payload.mitigationId),
            error,
            [401, 403, 404, 408]
          ),
          addToast({
            message: parsedError.message,
            type: 'error',
            id: 'Download_Email_Message_Error',
          })
        )
      })
  )

export default combineEpics(
  fetchDataEpic,
  clusterUndoEpic,
  downloadCsvEpic,
  stopRemediationEpic,
  revertBackToOriginEpic,
  downloadEmailEpic
)
